import React, { useState, useMemo } from 'react';
import targets from './salesDataTargets.json';
import InvoiceList from './InvoiceList';
import InvoiceSearch, { isSearching } from './InvoiceSearch';
import LoadStatus from './LoadStatus';
import SalesGraphs from './SalesGraphs';
import SalesTable from './SalesTable';
import { useInvoiceViewer } from './useInvoiceViewer';
import { useSalesInvoices } from './useSalesInvoices';
import { MONTHS } from './constants';
import { buildBuckets, getDrillInvoices, getUnplacedInvoices } from './salesCalculations';

const getDrillTitle = ({ personIndex, month }) => {
    const personName = personIndex === null ? 'Totalt' : targets.salespersons[personIndex].name;
    const period = month === null ? `Hela ${targets.year}` : `${MONTHS[month]} ${targets.year}`;
    return `${personName} - ${period}`;
};

const SalesData = () => {
    const { invoices, setInvoices, loading, progress, missingInvoices, fetchError } = useSalesInvoices();
    // personIndex / month are null when the drill-down covers all persons / all months
    const [drill, setDrill] = useState(null);
    // Invoice list opened from a graph, as { title, invoices }
    const [graphSelection, setGraphSelection] = useState(null);
    const [query, setQuery] = useState('');
    const { openInvoice, openOrder, refreshError, viewer } = useInvoiceViewer(setInvoices);

    const salesInvoices = useMemo(() => invoices.filter((invoice) =>
        !(invoice.ourReference || '').toLowerCase().includes('bft')), [invoices]);
    const buckets = useMemo(() => buildBuckets(salesInvoices), [salesInvoices]);
    const unplacedInvoices = useMemo(() => getUnplacedInvoices(salesInvoices, buckets), [salesInvoices, buckets]);
    const unplacedSet = useMemo(() => new Set(unplacedInvoices), [unplacedInvoices]);

    // The all-persons / all-months list also contains the invoices that are not part of the summary
    const getDrillList = (selection) => {
        const list = getDrillInvoices(buckets, selection);
        return selection.personIndex === null && selection.month === null
            ? [...list, ...unplacedInvoices]
            : list;
    };
    const invoiceList = graphSelection || (drill && { title: getDrillTitle(drill), invoices: getDrillList(drill) });

    return (
        <div style={{ margin: '2rem 0', padding: '0 1.5rem' }}>
            <LoadStatus loading={loading} progress={progress} fetchError={fetchError} missingInvoices={missingInvoices} refreshError={refreshError}>
                {invoiceList
                    ? (
                        <InvoiceList
                            title={invoiceList.title}
                            invoices={invoiceList.invoices}
                            unplacedInvoices={unplacedSet}
                            onBack={() => { setDrill(null); setGraphSelection(null); }}
                            onSelect={openInvoice}
                            onOpenOrder={openOrder}
                        />
                    )
                    : (
                        <>
                            <InvoiceSearch invoices={salesInvoices} query={query} onQueryChange={setQuery} onSelect={openInvoice} />

                            {!isSearching(query) && (
                                <>
                                    <SalesTable buckets={buckets} unplacedCount={unplacedInvoices.length} onDrill={setDrill} />
                                    <SalesGraphs buckets={buckets} onShowInvoices={setGraphSelection} />
                                </>
                            )}
                        </>
                    )}

                {viewer}
            </LoadStatus>
        </div>
    );
};

export default SalesData;
